import { FontWeight } from '@pasteltech/theme-provider'
import { memo, useEffect, useRef, useState } from 'react'
import ReactMarkDown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { styled } from 'styled-components'
import { getCasePostedAtString } from '../../helper'
import { CloseIcon } from '../../svg-components'
import { ScreenQuery, ScreenSize, useScreenSize } from '../../utilities'
import { DESKTOP_OVERLAY_VERTICAL_PADDING, Overlay } from '../overlay'

import { Case } from './case-card'
import { CaseDetailOverlaySwiper } from './case-detail-overlay-swiper'

const StyledOverlay = styled(Overlay)`
  border-radius: ${({ theme }) => theme.sizes.borderRadius.sm}px;
`

const Container = styled.div`
  display: flex;
  height: 100%;
  ${ScreenQuery.mobile} {
    flex-direction: column;
    overflow: auto;
  }
  ${ScreenQuery.tablet} {
    flex-direction: column;
    overflow: auto;
  }
`
const LeftContainer = styled.div`
  ${ScreenQuery.desktop} {
    flex: 0 0 60%;
  }
  background-color: ${({ theme }) => theme.colors.container.lowest};
`
const RightContainer = styled.div`
  ${ScreenQuery.desktop} {
    flex: 0 0 40%;
  }
  padding: ${({ theme }) => theme.sizes.spacings.xxxxl}px 0;
  & > * {
    width: 90%;
    margin: 0 auto;
  }
`
const MarkdownContainer = styled.div<{ $titleHeight: number }>`
  ${ScreenQuery.desktop} {
    overflow-y: auto;
    max-height: calc(
      100vh - ${DESKTOP_OVERLAY_VERTICAL_PADDING * 2}px -
        /* RightContainer padding */
        ${({ theme }) => theme.sizes.spacings.xxxxl * 2}px -
        ${({ $titleHeight }) => $titleHeight}px
    );
  }
  color: ${({ theme }) => theme.colors.text.medium};
`

const TitleAndDateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizes.spacings.xl}px;
`
const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`
const Title = styled.div`
  color: ${({ theme }) => theme.colors.text.high};
  font-size: ${({ theme }) => theme.sizes.fontSize.heading3}px;
  font-weight: ${FontWeight.bold};
`
const CaseCreatedDate = styled.div`
  color: ${({ theme }) => theme.colors.text.medium};
  font-size: ${({ theme }) => theme.sizes.fontSize.button}px;
  font-weight: ${FontWeight.regular};
`

const IconContainer = styled.button`
  &:hover {
    cursor: pointer;
  }
  border: none;
  background: transparent;
  height: fit-content;
  ${ScreenQuery.mobile} {
    position: absolute;
    top: 0px;
    right: 0px;
    z-index: 102;
  }
  ${ScreenQuery.tablet} {
    position: absolute;
    top: 0px;
    right: 0px;
    z-index: 102;
  }
`

type Props = {
  onClose: () => void
  post: Case
}

export const CaseDetailOverlay = memo(({ onClose, post }: Props) => {
  const view = useScreenSize()
  const titleContainerRef = useRef<HTMLDivElement>(null)
  const [titleHeight, setTitleHeight] = useState(0)
  // this is used to dynamically calculate the space left for markdown container
  useEffect(() => {
    if (!titleContainerRef.current) {
      return () => {}
    }
    const resizeObserver = new ResizeObserver(() => {
      setTitleHeight(titleContainerRef.current?.offsetHeight ?? 0)
    })
    resizeObserver.observe(titleContainerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <StyledOverlay onClose={onClose}>
      <Container>
        {view !== ScreenSize.desktop && (
          <IconContainer onClick={onClose}>
            <CloseIcon />
          </IconContainer>
        )}
        <LeftContainer>
          <CaseDetailOverlaySwiper images={post.images} videos={post.videos} />
        </LeftContainer>
        <RightContainer>
          <TitleAndDateContainer ref={titleContainerRef}>
            <TitleContainer>
              <Title>{post.title}</Title>
              {view === ScreenSize.desktop && (
                <IconContainer onClick={onClose}>
                  <CloseIcon />
                </IconContainer>
              )}
            </TitleContainer>
            <CaseCreatedDate>
              {getCasePostedAtString(post.postedAt)}
            </CaseCreatedDate>
          </TitleAndDateContainer>
          <MarkdownContainer $titleHeight={titleHeight}>
            <ReactMarkDown rehypePlugins={[rehypeRaw]}>
              {post?.content}
            </ReactMarkDown>
          </MarkdownContainer>
        </RightContainer>
      </Container>
    </StyledOverlay>
  )
})

CaseDetailOverlay.displayName = 'CaseDetailOverlay'
